<template>
  <div class="content-imagechoice">
    <!-- Title images section -->
    <div v-if="question.showImageOnTitle" class="title-images">
      <div v-for="(image, index) in question.imageOnTitle" :key="index" class="title-image-container"
        :class="[question.titleImageSize]" v-show="!image.isHidden">
        <div class="image-zoom-container">
          <img :src="image.label" :alt="image.value" class="title-image">
          <div class="zoom-indicator" @click="openImageModal(image)">
            <i class="material-icons">zoom_in</i>
          </div>
        </div>
        <div v-if="!question.hideTitleLabels" class="option-label">
          {{ image.value }}
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showModal" class="image-modal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-image-container">
          <img :src="selectedImage.label" :alt="selectedImage.value" class="modal-image">
          <div class="modal-close" @click="closeModal">
            <i class="material-icons">close</i>
          </div>
        </div>
      </div>
    </div>

    <!-- Subjects section -->
    <div class="subjects-container">
      <div v-for="(subject, subjectIndex) in question.subjects" :key="subjectIndex" :data-subject-id="subject._id"
        class="subject-section">
        <!-- Subject label above the grid -->
        <div class="subject-label">
          <h3>{{ subject.label }}</h3>
          <div class="selection-info">
            <span>{{ getSelectionCount(subject._id) }} / {{ subject.max }} {{ $t('selected') }}</span>
          </div>
        </div>
        <div class="options-grid" :class="[question.optionImageSize]">
          <div v-for="(option, optionIndex) in subject.options" :key="optionIndex" v-show="!option.isHidden"
            class="option-container" :class="{
              selected: isSelected(subject._id, optionIndex),
              'limit-reached': isSelectionLimitReached(subject) && !isSelected(subject._id, optionIndex)
            }" @click="handleOptionClick(subject, option, optionIndex)">
            <div class="image-wrapper">
              <img :src="option.label" :alt="option.value" class="option-image">
              <!-- Checkmark overlay for selected images -->
              <div v-if="isSelected(subject._id, optionIndex)" class="checkmark-overlay">
                <i class="material-icons">check_circle</i>
              </div>
              <!-- Limit reached overlay -->
              <div v-if="isSelectionLimitReached(subject) && !isSelected(subject._id, optionIndex)"
                class="limit-overlay">
                <i class="material-icons">lock</i>
              </div>
              <!-- Zoom indicator -->
              <div class="zoom-indicator" @click.stop="openImageModal(option)">
                <i class="material-icons">zoom_in</i>
              </div>
            </div>
            <div v-if="!question.hideAllSubjectLabels" class="option-label">
              {{ option.value }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { translator } from '@/_helpers'

export default {
  name: 'imagechoice',
  props: ['question', 'colors'],
  data () {
    return {
      selectedOptions: [], // Array of {subjectId, optionIndex, value}
      submitted: false,
      showModal: false,
      selectedImage: null
    }
  },
  computed: {
    isSkippable () {
      return !this.question.required
    }
  },
  created () {
    if (this.question.language) {
      this.$i18n.locale = translator.getCurrentLocale(this.question.language)
    }
  },
  mounted () {
    this.$root.$on('footer-submit', payload => {
      if (payload) {
        this.handleSubmit()
      }
    })
  },
  methods: {
    isSelected (subjectId, optionIndex) {
      return this.selectedOptions.some(sel =>
        sel.subjectId === subjectId && sel.optionIndex === optionIndex
      )
    },

    isSelectionLimitReached (subject) {
      const selections = this.selectedOptions.filter(sel => sel.subjectId === subject._id)
      return selections.length >= subject.max
    },

    selectOption (subjectId, option, optionIndex) {
      const subject = this.question.subjects.find(s => s._id === subjectId)
      if (!subject) return

      const existingSelections = this.selectedOptions.filter(sel => sel.subjectId === subjectId)
      const isCurrentlySelected = this.isSelected(subjectId, optionIndex)

      if (isCurrentlySelected) {
        // Remove if already selected
        this.selectedOptions = this.selectedOptions.filter(sel =>
          !(sel.subjectId === subjectId && sel.optionIndex === optionIndex)
        )
      } else {
        // Add new selection if within limits
        if (existingSelections.length < subject.max) {
          this.selectedOptions.push({
            subjectId: subjectId,
            optionIndex: optionIndex,
            optionId: option._id,
            value: option.value // Keep value for submission
          })

          // Check if we just reached the max for this subject
          if (existingSelections.length + 1 === subject.max) {
            // Find the next subject that needs answers
            const currentSubjectIndex = this.question.subjects.findIndex(s => s._id === subjectId)
            const nextIncompleteSubject = this.question.subjects.slice(currentSubjectIndex + 1)
              .find(s => {
                const selections = this.selectedOptions.filter(sel => sel.subjectId === s._id)
                return selections.length < s.max
              })

            // Use setTimeout to ensure the DOM has updated
            setTimeout(() => {
              if (nextIncompleteSubject) {
                // Scroll to next subject that needs answers
                const nextSubject = document.querySelector(`[data-subject-id="${nextIncompleteSubject._id}"]`)
                if (nextSubject) {
                  nextSubject.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              } else if (this.question.subjects.every(subject => this.isSubjectValid(subject))) {
                // If all subjects are complete, scroll to footer
                const footer = document.querySelector('.content-footer')
                if (footer) {
                  footer.scrollIntoView({ behavior: 'smooth', block: 'end' })
                }
              }
            }, 100)
          }
        }
      }

      // Check if all required subjects meet their requirements
      const allSubjectsValid = this.question.subjects.every(subject =>
        this.isSkippable || this.isSubjectValid(subject)
      )

      this.$root.$emit('show-btn', allSubjectsValid)
    },

    isSubjectValid (subject) {
      const selections = this.selectedOptions.filter(sel => sel.subjectId === subject._id)
      return selections.length >= subject.min && selections.length <= subject.max
    },

    handleSubmit () {
      if (this.isSkippable || this.question.subjects.every(subject => this.isSubjectValid(subject))) {
        // Format selections for submission
        const formattedSelections = this.selectedOptions.map(sel => ({
          _id: sel.subjectId,
          optionId: sel.optionId,
          value: sel.value
        }))
        this.submit({ value: formattedSelections })
      } else {
        this.submitted = true
      }
    },

    submit (data) {
      this.$emit('value-changed', data)
      this.$emit('question-answered', true)
    },

    getSelectionCount (subjectId) {
      const selections = this.selectedOptions.filter(sel => sel.subjectId === subjectId)
      return selections.length
    },

    handleOptionClick (subject, option, optionIndex) {
      if (this.isSelectionLimitReached(subject) && !this.isSelected(subject._id, optionIndex)) {
        return
      }
      this.selectOption(subject._id, option, optionIndex)
    },

    openImageModal (image) {
      this.selectedImage = image
      this.showModal = true
      document.body.style.overflow = 'hidden' // Prevent scrolling when modal is open
    },

    closeModal () {
      this.showModal = false
      document.body.style.overflow = 'auto' // Restore scrolling
    }
  }
}
</script>

<style lang="scss">
.content-imagechoice {
  width: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  .title-images {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
    margin-bottom: 40px;
    width: 100%;
    max-width: 800px;
    padding: 0 20px;
    justify-content: center;

    .title-image-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      margin: 0 auto;

      &.large {
        max-width: 400px;
        grid-column: span 2;

        .image-zoom-container {
          width: 100%;
          padding-top: 100%;
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          background-color: #f8f8f8;
        }

        .option-label {
          font-size: 1em;
          margin-top: 16px;
        }
      }

      &.medium {
        max-width: 280px;

        .image-zoom-container {
          width: 100%;
          padding-top: 100%;
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          background-color: #f8f8f8;
        }

        .option-label {
          font-size: 0.9em;
          margin-top: 14px;
        }
      }

      &.small {
        max-width: 200px;

        .image-zoom-container {
          width: 100%;
          padding-top: 100%;
          position: relative;
          border-radius: 6px;
          overflow: hidden;
          background-color: #f8f8f8;
        }

        .option-label {
          font-size: 0.85em;
          margin-top: 12px;
        }
      }

      .image-zoom-container {
        position: relative;
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          background: #f8f8f8;
          z-index: 1;
        }

        .title-image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 2;
          transition: transform 0.3s ease-out;
        }

        .zoom-indicator {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s ease, transform 0.2s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          z-index: 4;
          cursor: zoom-in;

          @media (hover: none) {
            opacity: 0.7;
            background: rgba(255, 255, 255, 0.8);
          }

          i {
            font-size: 20px;
            color: #666;
          }

          &:hover {
            transform: scale(1.1);
          }
        }

        // Show zoom indicator on hover for desktop
        @media (hover: hover) {
          &:hover {
            .zoom-indicator {
              opacity: 1;
            }

            .title-image {
              transform: scale(1.05);
            }
          }
        }
      }

      .option-label {
        margin-top: 16px;
        text-align: center;
        color: var(--splashColor);
        font-size: 0.95em;
        font-weight: 500;
        max-width: 90%;
      }
    }
  }

  .subjects-container {
    width: 100%;
    max-width: 800px;
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 20px 0;

    .subject-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: 40px 0;
      position: relative;

      .subject-label {
        width: 100%;
        margin-bottom: 30px;
        text-align: center;
        position: relative;

        h3 {
          font-size: 1.2em;
          color: var(--splashColor);
          margin: 0 0 12px 0;
          font-weight: 500;
          position: relative;
          display: inline-block;

          &::after {
            content: '';
            position: absolute;
            bottom: -6px;
            left: 50%;
            transform: translateX(-50%);
            width: 40px;
            height: 2px;
            background: var(--actionColor);
            border-radius: 2px;
            opacity: 0.7;
          }
        }

        .selection-info {
          font-size: 0.9em;
          color: var(--splashColor);
          opacity: 0.8;
          margin-top: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
      }

      .options-grid {
        display: grid;
        gap: 20px;
        width: 100%;
        max-width: 800px;
        justify-content: center;
        padding: 20px;

        &.small {
          grid-template-columns: repeat(auto-fit, minmax(110px, 130px));
          gap: 15px;
          justify-content: center;

          .option-container {

            .image-wrapper {
              border-radius: 6px;
            }

            .option-label {
              font-size: 0.85em;
            }
          }
        }

        &.medium {
          grid-template-columns: repeat(auto-fit, minmax(130px, 230px));
          gap: 20px;
          justify-content: center;

          .option-container {

            .image-wrapper {
              border-radius: 8px;
            }

            .option-label {
              font-size: 0.9em;
            }
          }
        }

        &.large {
          grid-template-columns: repeat(1, minmax(60px, 270px));

          gap: 25px;
          justify-content: center;

          .option-container {
            margin: 0 auto;

            .image-wrapper {
              border-radius: 12px;
            }

            .option-label {
              font-size: 1em;
              margin-top: 16px;
            }
          }
        }

        .option-container {
          width: 100%;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          transition: transform 0.2s ease;

          &.limit-reached {
            opacity: 0.6;
            cursor: not-allowed;
          }

          &:not(.limit-reached):not(.selected) {
            &:hover {
              .image-wrapper {
                .option-image {
                  transform: scale(1.05);
                }
              }
            }
          }

          .image-wrapper {
            width: 100%;
            aspect-ratio: 1;
            position: relative;
            margin-bottom: 12px;
            border-radius: 8px;
            overflow: hidden;
            background-color: #f8f8f8;
            transition: all 0.2s ease;

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: #f8f8f8;
              z-index: 1;
            }

            .option-image {
              position: absolute;
              inset: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              z-index: 2;
              transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            }

            .zoom-indicator {
              position: absolute;
              top: 6px;
              right: 6px;
              background: rgba(255, 255, 255, 0.9);
              border-radius: 50%;
              width: 26px;
              height: 26px;
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0;
              transition: opacity 0.2s ease, transform 0.2s ease;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              cursor: zoom-in;
              z-index: 4;

              @media (hover: none) {
                opacity: 0.7;
                background: rgba(255, 255, 255, 0.8);
              }

              i {
                font-size: 16px;
                color: #666;
              }

              &:hover {
                transform: scale(1.1);
              }
            }

            // Show zoom indicator on hover for desktop
            @media (hover: hover) {
              &:hover {
                .zoom-indicator {
                  opacity: 1;
                }
              }
            }

            .checkmark-overlay,
            .limit-overlay {
              position: absolute;
              inset: 0;
              background: rgba(0, 0, 0, 0.4);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 3;
              transition: all 0.3s ease;

              i {
                color: white;
                font-size: 36px;
                filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
              }
            }
          }

          &.selected .image-wrapper {
            border: 2px solid var(--actionColor);
            box-shadow: 0 0 0 2px rgba(var(--actionColor-rgb), 0.3);

            .option-image {
              transform: scale(1.05);
            }
          }

          .option-label {
            text-align: center;
            font-size: 0.9em;
            color: var(--splashColor);
            width: 100%;
            word-wrap: break-word;
          }
        }
      }
    }
  }

  .image-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 40px;
    animation: fadeIn 0.15s ease-out;
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);

    .modal-content {
      position: relative;
      max-width: 90%;
      max-height: 90%;
      display: flex;
      flex-direction: column;
      align-items: center;
      animation: zoomIn 0.15s ease-out;
      will-change: transform, opacity;

      .modal-image-container {
        position: relative;

        .modal-image {
          max-width: 100%;
          max-height: 70vh;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        }

        .modal-close {
          position: absolute;
          top: -40px;
          right: -40px;
          width: 30px;
          height: 30px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s ease-out;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

          i {
            font-size: 24px;
            color: #333;
          }

          &:hover {
            background: #fff;
            transform: scale(1.05);
          }
        }

      }

    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.98);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media only screen and (max-width: 850px) {
  .content-imagechoice {
    padding: 15px;

    .title-images {
      gap: 20px;
      padding: 0;
      margin-bottom: 30px;

      .title-image-container {
        &.large {
          grid-template-columns: repeat(1, 1fr);
          max-width: 320px;
          grid-column: span 2;
        }

        &.medium {
          max-width: 220px;
          grid-template-columns: repeat(2, 1fr);
        }

        &.small {
          grid-template-columns: repeat(4, 1fr);
          max-width: 160px;
        }

        .option-label {
          margin-top: 12px;
          font-size: 0.9em;
        }
      }
    }

    .options-grid {
      padding: 15px;

      &.small {
        grid-template-columns: repeat(auto-fit, minmax(100px, 130px)) !important;
      }

      &.medium {
        grid-template-columns: repeat(auto-fit, minmax(130px, 245px)) !important;
        gap: 15px;
      }

      &.large {
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 20px;
      }
    }
  }

  .subjects-container {
    .subject-section {
      padding: 30px 0;

      &:nth-child(even) {
        padding: 30px 15px;
        margin: 8px 0;
      }

      .subject-label h3 {
        font-size: 1.1em;
      }
    }
  }
}

@media only screen and (max-width: 580px) {
  .content-imagechoice {
    padding: 10px;

    .title-images {
      gap: 15px;
      margin-bottom: 25px;

      .title-image-container {
        &.large {
          max-width: 280px;
        }

        &.medium {
          max-width: 180px;
        }

        &.small {
          max-width: 140px;
        }

        .option-label {
          margin-top: 8px;
          font-size: 0.85em;
        }
      }
    }

    .options-grid {
      padding: 10px;

      &.small {
        grid-template-columns: repeat(auto-fit, minmax(85px, 100px));
        gap: 10px;
      }

      &.medium {
        grid-template-columns: repeat(auto-fit, minmax(120px, 165px))  !important;
        gap: 12px;
      }

      &.large {
        grid-template-columns: repeat(auto-fit, minmax(90px, 235px))  !important;
        gap: 15px;
      }
    }
  }

  .subjects-container {
    .subject-section {
      padding: 25px 0;

      &:nth-child(even) {
        padding: 25px 10px;
        margin: 5px 0;
      }

      .subject-label h3 {
        font-size: 1em;
      }
    }
  }
}

@media only screen and (max-width: 420px) {
  .content-imagechoice {
    .options-grid {
      &.small {
        .option-container {
          max-width: 130px;
        }
      }
      &.medium {
        grid-template-columns: repeat(auto-fit, minmax(90px, 120px))  !important;
      }
      &.large {
        grid-template-columns: repeat(auto-fit, minmax(90px, 160px))  !important;
      }
    }
  }
}
</style>
